/**
 * EndpointRow - Renders a single endpoint row in a graph node
 *
 * Shows input/output handles, direction indicators, and the control widget.
 * Audio endpoints use RAF loop to update visualization directly (bypassing React).
 */

import { useEffect, useRef, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { EndpointControl } from './EndpointControl';
import { TweakableExposedToggle } from './TweakableExposedToggle';
import { Tooltip } from '@/ui/shared/Tooltip';

import { getEndpointColor } from '../../shared/utils';

import { EndpointState } from '@/nodes/Endpoint';
import { useEndpointActivity } from '@/ui/components/graph/useEndpointActivity';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { webRuntimeApi } from '@/runtime/web';

// Layout constants - shared with GraphNode
export const ROW_PADDING = 4;
export const ROW_CONTENT = 20;
export const ROW_BORDER = 1;
export const ROW_HEIGHT = ROW_PADDING + ROW_CONTENT + ROW_PADDING + ROW_BORDER;

// Convert camelCase to space-separated words (e.g., "modulationIndex" -> "Modulation index")
function camelToSpaceCase(str: string): string {
  const spaced = str.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export interface EndpointRowProps {
  instanceId: string;
  nodeId: string;
  nodeType: string;
  endpoint: EndpointState;
  handleTop: number;
  isCollapsed?: boolean;  // True when endpoint's group is collapsed
  hasConnections?: boolean;  // True if endpoint has any connections
}

export const EndpointRow = memo(function EndpointRow({
  instanceId,
  nodeId,
  endpoint,
  handleTop,
  isCollapsed = false,
  hasConnections = false,
}: EndpointRowProps) {
  const activeEndpoints = useEndpointActivity();
  const isPreset = endpoint.id === 'preset';

  const displayName = endpoint.label || camelToSpaceCase(endpoint.id);
  const { getEndpointValue } = useRuntime()

  // Destructure endpoint defaults
  const {
    isInput,
    isOutput,
    isDisabled,
  } = endpoint;


  // Derive color from endpoint dataFlow (trigger/event) or dataType
  const color = getEndpointColor(endpoint.dataType, endpoint.dataFlow);
  const isAudioEndpoint = endpoint.dataType === 'audio';



  // Refs for direct DOM manipulation of audio handles
  const inputHandleRef = useRef<HTMLDivElement>(null);
  const outputHandleRef = useRef<HTMLDivElement>(null);

  // Pre-build endpoint key to avoid string concat in RAF loop
  const endpointKey = `${nodeId}.${endpoint.id}`;

  // RAF loop for audio visualization - bypasses React render cycle
  useEffect(() => {
    if (!isAudioEndpoint) return;

    let rafId: number;
    let stopped = false;

    const draw = () => {
      if (stopped) return;

      const level = getEndpointValue(instanceId, endpointKey) ?? 0;
      // Gradation: interpolate toward white based on level
      // RMS values are typically 0.01-0.2, so scale up aggressively
      const t = Math.min(1, level * 8);  // Full white at level ~0.125
      const bg = `color-mix(in srgb, ${color}, white ${Math.round(t * 100)}%)`;
      const shadow = level > 0.005 ? `0 0 ${4 + t * 6}px ${t * 3}px ${color}` : 'none';

      if (inputHandleRef.current) {
        inputHandleRef.current.style.background = bg;
        inputHandleRef.current.style.boxShadow = shadow;
        inputHandleRef.current.style.transition = 'none';
      }
      if (outputHandleRef.current) {
        outputHandleRef.current.style.background = bg;
        outputHandleRef.current.style.boxShadow = shadow;
        outputHandleRef.current.style.transition = 'none';
      }

      rafId = requestAnimationFrame(draw);
    };

    // Stop loop when project is about to be disposed
    const unsubDispose = webRuntimeApi.subscribe.beforeDispose(() => {
      stopped = true;
      cancelAnimationFrame(rafId);
    });

    rafId = requestAnimationFrame(draw);
    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      unsubDispose();
    };
  }, [isAudioEndpoint, instanceId, nodeId, color]);

  const endpointBlinkTransition = 'background 0.02s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.02s cubic-bezier(0.16, 1, 0.3, 1)'

  const inputActive = activeEndpoints.has(`${nodeId}.${endpoint.id}:in`);
  const outputActive = activeEndpoints.has(`${nodeId}.${endpoint.id}:out`);

  return (
    <div
      className={isDisabled ? 'disabled' : undefined}
      style={{
        borderBottom: `${ROW_BORDER}px solid #333`,
        background: isPreset ? '#374151' : undefined,
      }}
    >
      {/* Main row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: ROW_HEIGHT - ROW_BORDER,
          padding: '0 3px',
          boxSizing: 'content-box'
        }}
      >
        {/* Input handle - hide when collapsed unless has connections */}
        {isInput && (!isCollapsed || hasConnections) && (
          <Handle
            ref={isAudioEndpoint ? inputHandleRef : undefined}
            type="target"
            position={Position.Left}
            id={endpoint.id}
            style={{
              top: handleTop,
              left: isCollapsed ? -4 : -6,
              background: inputActive ? '#fff' : color,
              width: isCollapsed ? 4 : 11,
              height: isCollapsed ? 8 : 15,
              border: 'none',
              borderRadius: '50% 0 0 50%',
              boxShadow: inputActive && !isCollapsed ? `0 0 8px 3px ${color}` : 'none',
              transition: isAudioEndpoint ? 'none' : endpointBlinkTransition
            }}
          />
        )}

        {/* Tweakable/exposed toggle + name */}
        {!isPreset ? (
          <TweakableExposedToggle instanceId={instanceId} nodeId={nodeId} endpoint={endpoint}>
            <Tooltip text={endpoint.info}>
              <span
                style={{
                  color: (endpoint.isExposed || endpoint.tweakable) ? 'white' : '#9ca3af',
                  fontSize: 11,
                  paddingLeft: '3px',
                  minWidth: 80,
                  maxWidth: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </span>
            </Tooltip>
          </TweakableExposedToggle>
        ) : (
          <Tooltip text={endpoint.info}>
            <span
              style={{
                color: '#9ca3af',
                fontSize: 11,
                paddingLeft: '3px',
                minWidth: 80,
                maxWidth: 100,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </span>
          </Tooltip>
        )}

        {/* Control */}
        <EndpointControl
          instanceId={instanceId}
          nodeId={nodeId}
          endpoint={endpoint}
        />

        {/* Output handle - hide when collapsed unless has connections */}
        {isOutput && (!isCollapsed || hasConnections) && (
          <Handle
            ref={isAudioEndpoint ? outputHandleRef : undefined}
            type="source"
            position={Position.Right}
            id={endpoint.id}
            style={{
              top: handleTop,
              right: isCollapsed ? -4 : -6,
              background: outputActive ? '#fff' : color,
              width: isCollapsed ? 4 : 11,
              height: isCollapsed ? 8 : 15,
              border: 'none',
              borderRadius: '0 50% 50% 0',
              boxShadow: outputActive && !isCollapsed ? `0 0 8px 3px ${color}` : 'none',
              transition: isAudioEndpoint ? 'none' : endpointBlinkTransition
            }}
          />
        )}
      </div>

    </div>
  );
});
